import { useCallback, useRef, useState, useEffect } from 'react';
import type { FilterLine } from '../components/new-design/vyTableFilter/types';
import { getValidFilters } from '../components/new-design/vyTableFilter/vyTableFilterUtils';

const DEBOUNCE_DELAY_MS = 500;

type UseTableFiltersOptions = {
  defaultFilters?: FilterLine[];
  onFiltersChange?: (filters: FilterLine[]) => void;
};

type UseTableFiltersReturn = {
  anchorRef: React.MutableRefObject<HTMLElement | null>;
  isOpen: boolean;
  appliedFilters: number;
  handleClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleClose: () => void;
  handleFiltersChange: (filters: FilterLine[]) => void;
};

export function useTableFilters(options: UseTableFiltersOptions = {}): UseTableFiltersReturn {
  const { defaultFilters = [], onFiltersChange } = options;
  const anchorRef = useRef<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<number>(0);
  const [pendingFilters, setPendingFilters] = useState<FilterLine[]>([]);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasUserInteractedRef = useRef<boolean>(false);

  useEffect(() => {
    const validFilters = getValidFilters(defaultFilters);
    setAppliedFilters(validFilters.length);
  }, [defaultFilters]);

  useEffect(() => {
    // Skip the debounce on the initial render — pendingFilters starts as [] but the URL
    // (and `defaultFilters`) may already have filters; firing onFiltersChange([]) here
    // would clobber them. Only debounce once the user has actually changed something.
    if (!hasUserInteractedRef.current) {
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      const validFilters = getValidFilters(pendingFilters);
      onFiltersChange?.(validFilters);
      debounceTimeoutRef.current = null;
    }, DEBOUNCE_DELAY_MS);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [pendingFilters, onFiltersChange]);

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    anchorRef.current = event.currentTarget;
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleFiltersChange = useCallback((filters: FilterLine[]) => {
    hasUserInteractedRef.current = true;
    const validFilters = getValidFilters(filters);
    setAppliedFilters(validFilters.length);
    setPendingFilters(filters);
  }, []);

  return {
    anchorRef,
    isOpen,
    appliedFilters,
    handleClick,
    handleClose,
    handleFiltersChange,
  };
}
